/**
 * PortalDenuncias.js - Portal de Denúncias para Empresa/RH
 * Visualização e resposta a denúncias anônimas recebidas
 * IMPORTANTE: A identidade do denunciante é completamente oculta
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Shield,
  ShieldAlert,
  AlertTriangle,
  MessageSquare,
  Clock,
  CheckCircle,
  Eye,
  FileText,
  Send,
  Filter,
  Search,
  RefreshCw,
  ChevronRight,
  Lock,
  Info,
  BarChart3,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Status das denúncias
const STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
  em_analise: { label: 'Em Análise', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Eye },
  respondida: { label: 'Respondida', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
  arquivada: { label: 'Arquivada', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText }
};

// Tipos de denúncia com ícones
const TIPOS_DENUNCIA = {
  assedio_moral: { label: 'Assédio Moral', icon: '😢', priority: 'alta' },
  assedio_sexual: { label: 'Assédio Sexual', icon: '🚫', priority: 'critica' },
  discriminacao: { label: 'Discriminação', icon: '⚖️', priority: 'alta' },
  fraude: { label: 'Fraude/Corrupção', icon: '💰', priority: 'critica' },
  seguranca: { label: 'Segurança do Trabalho', icon: '⚠️', priority: 'alta' },
  etica: { label: 'Violação de Ética', icon: '📋', priority: 'media' },
  conflito_interesse: { label: 'Conflito de Interesse', icon: '🔄', priority: 'media' },
  outros: { label: 'Outros', icon: '📝', priority: 'baixa' }
};

export default function PortalDenuncias() {
  const { user, selectedCompany } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  
  // Modal de detalhes
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);

  // Carregar denúncias
  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${API}/api/company/complaints`;
      const params = [];
      if (filterStatus && filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (filterTipo && filterTipo !== 'all') params.push(`tipo=${filterTipo}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComplaints(response.data);
    } catch (error) {
      console.error('Erro ao carregar denúncias:', error);
      toast.error('Erro ao carregar denúncias');
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/company/complaints/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  useEffect(() => {
    fetchComplaints();
    fetchStats();
  }, [filterStatus, filterTipo]);

  // Abrir detalhes da denúncia
  const openDetail = async (complaint) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/company/complaints/${complaint.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedComplaint(response.data);
      setShowDetailModal(true);
      setResponseText('');
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes da denúncia');
    }
  };

  // Enviar resposta
  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Digite uma resposta');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/company/complaints/${selectedComplaint.id}/respond`,
        { mensagem: responseText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Resposta enviada ao denunciante');
      setResponseText('');
      
      // Recarregar detalhes e lista
      const response = await axios.get(
        `${API}/api/company/complaints/${selectedComplaint.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedComplaint(response.data);
      fetchComplaints();
      fetchStats();
      
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  // Atualizar status
  const updateStatus = async (complaintId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/company/complaints/${complaintId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status atualizado');
      fetchComplaints();
      fetchStats();
      
      if (selectedComplaint?.id === complaintId) {
        setSelectedComplaint(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  // Filtrar denúncias por busca
  const filteredComplaints = complaints.filter(c => {
    if (activeTab === 'pendentes' && c.status !== 'pendente') return false;
    if (activeTab === 'em_analise' && c.status !== 'em_analise') return false;
    if (activeTab === 'respondidas' && c.status !== 'respondida') return false;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        c.descricao?.toLowerCase().includes(search) ||
        c.tipo_label?.toLowerCase().includes(search) ||
        c.departamento?.toLowerCase().includes(search) ||
        c.codigo_acompanhamento?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Formatação de data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="portal-denuncias">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-amber-600" />
              Portal de Denúncias
            </h1>
            <p className="text-slate-500 mt-1">
              Canal seguro e 100% anônimo para colaboradores
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => { fetchComplaints(); fetchStats(); }}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>

        {/* Aviso de Anonimato */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Proteção Total da Identidade
              </p>
              <p className="text-sm text-amber-700 mt-1">
                As denúncias exibidas aqui são <strong>completamente anônimas</strong>. 
                Você não tem acesso a nenhuma informação que identifique o denunciante. 
                Responda com seriedade e profissionalismo.
              </p>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-700">{stats.by_status?.pendente || 0}</p>
                    <p className="text-xs text-yellow-600">Pendentes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{stats.by_status?.em_analise || 0}</p>
                    <p className="text-xs text-blue-600">Em Análise</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">{stats.by_status?.respondida || 0}</p>
                    <p className="text-xs text-green-600">Respondidas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-700">{stats.by_status?.arquivada || 0}</p>
                    <p className="text-xs text-slate-600">Arquivadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros e Busca */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por descrição, tipo, departamento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {Object.entries(TIPOS_DENUNCIA).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      {val.icon} {val.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs de Status */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start">
            <TabsTrigger value="todas">
              Todas ({complaints.length})
            </TabsTrigger>
            <TabsTrigger value="pendentes" className="text-yellow-700">
              Pendentes ({complaints.filter(c => c.status === 'pendente').length})
            </TabsTrigger>
            <TabsTrigger value="em_analise" className="text-blue-700">
              Em Análise ({complaints.filter(c => c.status === 'em_analise').length})
            </TabsTrigger>
            <TabsTrigger value="respondidas" className="text-green-700">
              Respondidas ({complaints.filter(c => c.status === 'respondida').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredComplaints.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhuma denúncia encontrada</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredComplaints.map((complaint) => {
                  const tipoConfig = TIPOS_DENUNCIA[complaint.tipo] || TIPOS_DENUNCIA.outros;
                  const statusConfig = STATUS_CONFIG[complaint.status] || STATUS_CONFIG.pendente;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Card 
                      key={complaint.id}
                      className="hover:border-amber-300 transition-colors cursor-pointer"
                      onClick={() => openDetail(complaint)}
                      data-testid={`complaint-card-${complaint.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Ícone do tipo */}
                          <div className="text-3xl flex-shrink-0">
                            {tipoConfig.icon}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Header */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="font-medium">
                                {tipoConfig.label}
                              </Badge>
                              <Badge className={`${statusConfig.color} border`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              {complaint.departamento && (
                                <Badge variant="secondary" className="text-xs">
                                  {complaint.departamento}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Descrição (truncada) */}
                            <p className="text-slate-700 mt-2 line-clamp-2 text-sm">
                              {complaint.descricao}
                            </p>
                            
                            {/* Footer */}
                            <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(complaint.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {complaint.respostas?.length || 0} respostas
                              </span>
                            </div>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Modal de Detalhes */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedComplaint && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-600" />
                    Denúncia Anônima
                  </DialogTitle>
                  <DialogDescription>
                    Código: {selectedComplaint.codigo_acompanhamento}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Info do Anonimato */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
                    <Lock className="w-4 h-4 text-slate-500 mt-0.5" />
                    <p className="text-xs text-slate-600">
                      A identidade do denunciante está protegida. Você não tem acesso a dados pessoais.
                    </p>
                  </div>
                  
                  {/* Tipo e Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Tipo</Label>
                      <p className="font-medium flex items-center gap-2 mt-1">
                        <span className="text-xl">
                          {TIPOS_DENUNCIA[selectedComplaint.tipo]?.icon || '📝'}
                        </span>
                        {selectedComplaint.tipo_label || selectedComplaint.tipo}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Select
                        value={selectedComplaint.status}
                        onValueChange={(val) => updateStatus(selectedComplaint.id, val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <SelectItem key={key} value={key}>
                              {val.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Departamento e Data */}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedComplaint.departamento && (
                      <div>
                        <Label className="text-xs text-slate-500">Departamento</Label>
                        <p className="font-medium mt-1">{selectedComplaint.departamento}</p>
                      </div>
                    )}
                    {selectedComplaint.data_ocorrencia && (
                      <div>
                        <Label className="text-xs text-slate-500">Data do Ocorrido</Label>
                        <p className="font-medium mt-1">
                          {new Date(selectedComplaint.data_ocorrencia).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Descrição */}
                  <div>
                    <Label className="text-xs text-slate-500">Descrição</Label>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-1">
                      <p className="text-slate-700 whitespace-pre-wrap">
                        {selectedComplaint.descricao}
                      </p>
                    </div>
                  </div>
                  
                  {/* Histórico de Respostas */}
                  {selectedComplaint.respostas && selectedComplaint.respostas.length > 0 && (
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">
                        Histórico de Respostas ({selectedComplaint.respostas.length})
                      </Label>
                      <div className="space-y-2">
                        {selectedComplaint.respostas.map((resp, idx) => (
                          <div 
                            key={idx} 
                            className="bg-amber-50 border border-amber-200 rounded-lg p-3"
                          >
                            <p className="text-slate-700 text-sm">{resp.mensagem}</p>
                            <p className="text-xs text-amber-700 mt-2">
                              Respondido em {formatDate(resp.data)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Nova Resposta */}
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium">Enviar Resposta ao Denunciante</Label>
                    <Textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Digite sua resposta. O denunciante poderá visualizá-la através do código de acompanhamento..."
                      className="mt-2 min-h-[100px]"
                    />
                    <Button
                      onClick={handleSendResponse}
                      disabled={sending || !responseText.trim()}
                      className="mt-3 bg-amber-600 hover:bg-amber-700"
                    >
                      {sending ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Enviar Resposta
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
